import { useState, useCallback } from "react";
import { Dayjs } from "dayjs";

import {
  Invoice,
  InvoicePayload,
  InvoiceCompanyHeaderInfo,
} from "../bitmaker/schemas/Billing/Invoice";

import { useApiError } from "./useApiError";

import { methodMap, HTTPMethod } from "../services/BitmakerApp";

import { cleanValue } from "../utils";

export function useSaveInvoice(invoiceId: string | undefined) {
  const { handleAPIError } = useApiError();

  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [saveInvoiceError, setSaveInvoiceError] = useState<string | null>(null);

  const saveInvoice = useCallback(
    async (
      values: any,
      headerDisplayInfo: InvoiceCompanyHeaderInfo,
    ): Promise<Invoice | null> => {
      setIsSavingInvoice(true);
      setSaveInvoiceError(null);

      const { amountDue, amountPaid, invoiceNumber, ...invoiceValues } = values;

      const convertedItems = invoiceValues.items.map(
        (item: any, index: number) => {
          const convertedTaxes = item.appliedTaxes
            ? item.appliedTaxes.map((tax: any) => ({
              ...tax,
              taxRate: parseFloat(tax.taxRate),
            }))
            : [];

          return {
            ...item,
            itemName: cleanValue(item.itemName),
            itemDescription: cleanValue(item.itemDescription),
            unitPrice: parseFloat(item.unitPrice),
            quantity: parseFloat(item.quantity),
            sortOrder: index + 1,
            appliedTaxes: convertedTaxes,
          };
        },
      );

      const payload: InvoicePayload = {
        ...headerDisplayInfo,
        companyId: invoiceValues.companyId,
        clientId: invoiceValues.clientId,
        clientAddressLine: cleanValue(invoiceValues.clientAddressLine),
        clientCompanyName: cleanValue(invoiceValues.clientCompanyName),
        clientCorporateName: cleanValue(invoiceValues.clientCorporateName),
        clientCountry: cleanValue(invoiceValues.clientCountry),
        clientEmailAddress: cleanValue(invoiceValues.clientEmailAddress),
        clientFirstName: cleanValue(invoiceValues.clientFirstName),
        clientLastName: cleanValue(invoiceValues.clientLastName),
        clientPhoneNumber: cleanValue(invoiceValues.clientPhoneNumber),
        clientPostalCode: cleanValue(invoiceValues.clientPostalCode),
        clientStateProvince: cleanValue(invoiceValues.clientStateProvince),
        clientTownCity: cleanValue(invoiceValues.clientTownCity),
        clientTaxName: cleanValue(invoiceValues.clientTaxName),
        clientTaxNumber: cleanValue(invoiceValues.clientTaxNumber),
        items: convertedItems,
        // Convert Dayjs to string (YYYY-MM-DD format) for the API
        issueDate: (invoiceValues.issueDate as Dayjs).format("YYYY-MM-DD"),
        dueDate: (invoiceValues.dueDate as Dayjs).format("YYYY-MM-DD"),
        notes: cleanValue(invoiceValues.notes),
        reference: cleanValue(invoiceValues.reference),
        terms: cleanValue(invoiceValues.terms),
        termId:
          invoiceValues.termId === null ||
            invoiceValues.termId === undefined ||
            invoiceValues.termId === 0
            ? null
            : invoiceValues.termId,
        language: invoiceValues.language || "en",
        currency: invoiceValues.currency || "USD",
      };

      const endpoint = invoiceId
        ? { method: "put", url: `/billing/invoices/${invoiceId}` }
        : { method: "post", url: "/billing/invoices" };

      return new Promise((resolve) => {
        methodMap[endpoint.method as HTTPMethod](
          endpoint.url,
          payload,
          (response) => {
            const { isError, message } = handleAPIError(response);

            setIsSavingInvoice(false);

            if (isError) {
              console.error(
                `Error ${invoiceId ? "updating" : "creating"} invoice:`,
                message,
              );
              setSaveInvoiceError(
                `Error ${invoiceId ? "updating" : "creating"
                } invoice. Please try again.`,
              );
              resolve(null);
              return;
            }

            resolve(response.data as Invoice);
          },
        );
      });
    },
    [invoiceId, handleAPIError],
  );

  return {
    saveInvoice,
    isSavingInvoice,
    saveInvoiceError,
  };
}
